import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';

import ExternalFileOptions from 'external-files/ExternalFileOptions';
import ExternalFilePreview from 'external-files/ExternalFilePreview';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import FileStates from 'FileStates';
import * as navigation from 'Navigation';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';

import {ResultBlobGraphQL} from '../../../graphql-fragments/results/__generated/ResultBlobGraphQL';

class BlobLinkTabState extends navigation.TabState {
  blob(): ResultBlobGraphQL {
    return this.get('blob');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override defaults(): any {
    return {
      ...super.defaults(),
      title: 'Preview File',
      closeable: true,
      blob: null,
      previewType: null,
    };
  }

  static open(fileId: string, blob: ResultBlobGraphQL, owningEntityApiId: string): void {
    if (blob.previewFileJson == null && blob.pdfPreviewFileJson == null) {
      throw new Error('Attempted to preview a blob without a previewFileJson');
    }

    const fileState = FileStates.get().get(fileId);
    const existingTab = fileState.findTab(
      BlobLinkTabState,
      (tab: BlobLinkTabState) => tab.blob().id === blob.id
    );
    if (existingTab != null) {
      existingTab.set({active: true});
      return;
    }

    const newTab = new BlobLinkTabState({
      fileId,
      title: blob.name,
      blob,
      previewType: blob.previewFileJson
        ? blob.previewFileJson.previewType
        : blob.pdfPreviewFileJson?.previewType,
      owningEntityApiId,
    });
    fileState.buffers().addTab(newTab);
  }
}

class BlobLinkTabView extends Backbone.View<BlobLinkTabState> {
  override initialize(): void {
    this.listenTo(this.model, 'change', () => this.render());
  }

  override render(): this {
    const previewType = this.model.get('previewType');
    const owningEntityApiId = this.model.get('owningEntityApiId');
    const {previewFileJson, pdfPreviewFileJson} = this.model.blob();
    const chosenFile = previewType === 'image' ? previewFileJson : pdfPreviewFileJson;
    if (chosenFile == null) {
      throw new Error('previewFileJson is null');
    }
    renderToDOM(
      <ExternalFilePreview
        url={chosenFile.url}
        externalFileId={chosenFile.id}
        previewType={this.model.get('previewType')}
        owningEntityApiId={owningEntityApiId}
      />,
      this.el
    );
    return this;
  }

  override remove(): Backbone.View<BlobLinkTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

class BlobLinkOptionsView extends Backbone.View<BlobLinkTabState> {
  override initialize(): void {
    this.listenTo(this.model, 'change', () => this.render());
  }

  updatePreviewType = (previewType: string): void => {
    this.model.set('previewType', previewType);
  };

  override render = (): this => {
    const previewType = this.model.get('previewType');
    const owningEntityApiId = this.model.get('owningEntityApiId');
    const {url, previewFileJson, pdfPreviewFileJson} = this.model.blob();
    const chosenFile = previewType === 'image' ? previewFileJson : pdfPreviewFileJson;
    if (chosenFile == null) {
      throw new Error('previewFileJson is null');
    }

    renderToDOM(
      <ExternalFileOptions
        availablePreviewTypes={[
          ...(previewFileJson?.availablePreviewTypes ?? []),
          ...(pdfPreviewFileJson?.availablePreviewTypes ?? []),
        ]}
        previewType={previewType}
        updatePreviewType={this.updatePreviewType}
        printOptions={[]}
        generateDownloadUrl={async (isPreview: boolean) =>
          isPreview ? await performSignedFileQuery(chosenFile.id, chosenFile.url, owningEntityApiId) : url
        }
        isPreviewTransformed={false}
        closeTab={() => {
          this.model.close();
        }}
      />,
      this.el
    );
    return this;
  };

  override remove(): Backbone.View<BlobLinkTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

BlobLinkTabView.prototype.className = 'external-file-wrapper buffer-tab buffer-tab--no-padding';

export {BlobLinkTabState as tabState, BlobLinkTabView as tabView, BlobLinkOptionsView as optionsView};
